import React, { useEffect, useState, useCallback } from "react";
import {
  getDashboardStats,
  getDashboardActivity,
  getClients,
  getLetters,
  getDisputes,
  getAnalyticsOverview,
  getActivityLog,
  getCurrentRole,
} from "../api";

export default function Dashboard() {
  const [stats, setStats] = useState({
    total_clients: 0,
    total_letters: 0,
    total_disputes: 0,
    letters_last_30_days: 0,
    total_notes_tasks: 0,
    total_documents: 0,
    completed_tasks: 0,
    open_disputes: 0,
    pending_disputes: 0,
    resolved_disputes: 0,
    activity_last_7_days: 0,
  });

  const [analytics, setAnalytics] = useState({
    summary: {},
    letters_by_day: [],
    tasks_by_status: [],
    disputes_by_bureau: [],
    documents_by_type: [],
  });

  const [recentClients, setRecentClients] = useState([]);
  const [recentLetters, setRecentLetters] = useState([]);
  const [recentDisputes, setRecentDisputes] = useState([]);
  const [activity, setActivity] = useState({
    tasks_due: [],
    recent_letters: [],
  });
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setMessage("");

    try {
      const currentRole = getCurrentRole();

      let currentCompany = {};

      try {
        currentCompany = JSON.parse(
          localStorage.getItem("company") || "{}"
        );
      } catch {
        currentCompany = {};
      }

      const currentPlan = String(
        currentCompany?.subscription_plan || ""
      ).toLowerCase();

      const canViewAnalytics =
        ["admin", "manager"].includes(currentRole) &&
        ["professional", "white_label"].includes(currentPlan);

      const [
        statsData,
        clientsData,
        lettersData,
        disputesData,
        analyticsData,
        activityData,
      ] = await Promise.all([
        getDashboardStats(),
        getClients(),
        getLetters(),
        getDisputes(),
        canViewAnalytics
          ? getAnalyticsOverview()
          : Promise.resolve(null),
        getActivityLog(), 
      ]);

  const dashboardActivityData = await getDashboardActivity();
  setActivity(dashboardActivityData);

      setStats({
        total_clients: statsData?.total_clients || 0,
        total_letters: statsData?.total_letters || 0,
        total_disputes: statsData?.total_disputes || 0,
        letters_last_30_days: statsData?.letters_last_30_days || 0,
        total_notes_tasks: statsData?.total_notes_tasks || 0,
        total_documents: statsData?.total_documents || 0,
        completed_tasks: statsData?.completed_tasks || 0,
        open_disputes: statsData?.open_disputes || 0,
        pending_disputes: statsData?.pending_disputes || 0,
        resolved_disputes: statsData?.resolved_disputes || 0,
        activity_last_7_days: statsData?.activity_last_7_days || 0,
      });

      setAnalytics({
        summary: analyticsData?.summary || {},
        letters_by_day: Array.isArray(analyticsData?.letters_by_day)
          ? analyticsData.letters_by_day
          : [],
        tasks_by_status: Array.isArray(analyticsData?.tasks_by_status)
          ? analyticsData.tasks_by_status
          : [],
        disputes_by_bureau: Array.isArray(analyticsData?.disputes_by_bureau)
          ? analyticsData.disputes_by_bureau
          : [],
        documents_by_type: Array.isArray(analyticsData?.documents_by_type)
          ? analyticsData.documents_by_type
          : [],
      });

      const clientsList = Array.isArray(clientsData) ? clientsData : [];
      const lettersList = Array.isArray(lettersData) ? lettersData : [];
      const disputesList = Array.isArray(disputesData) ? disputesData : [];

      setRecentClients(clientsList.slice(0, 5));
      setRecentLetters(lettersList.slice(0, 5));
      setRecentDisputes(disputesList.slice(0, 5));
    } catch (error) {
      setMessage(error.message || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    function handleDashboardRefresh() {
      loadDashboard();
    }

    function handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        loadDashboard();
      }
    }

    function handleWindowFocus() {
      loadDashboard();
    }

    window.addEventListener("integrity-dashboard-refresh", handleDashboardRefresh);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleWindowFocus);

    return () => {
      window.removeEventListener("integrity-dashboard-refresh", 
handleDashboardRefresh);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [loadDashboard]);

  const completionPercent =
    stats.total_notes_tasks > 0
      ? Math.min(100, Math.round((stats.completed_tasks / stats.total_notes_tasks) * 
100))
      : 0;

  return (
    <div style={{ padding: "28px", background: "#f3f6fb", minHeight: "100vh" }}>
      <div
        style={{
          marginBottom: "28px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "20px",
          flexWrap: "wrap",
        }}
      >
        <div>
          <div
            style={{
              fontSize: "18px",
              color: "#6b7280",
              marginBottom: "8px",
              fontWeight: "500",
            }}
          >
            Integrity Suite CRM
          </div>
          <h1
            style={{
              fontSize: "64px",
              fontWeight: "800",
              margin: 0,
              lineHeight: 1,
              color: "#0f172a",
            }}
          >
            Dashboard
          </h1>
        </div>

        <button
          onClick={loadDashboard}
          style={{
            padding: "16px 24px",
            borderRadius: "16px",
            border: "none",
            background: "#0f172a",
            color: "white",
            fontSize: "17px",
            fontWeight: "600",
            cursor: "pointer",
            boxShadow: "0 8px 20px rgba(15,23,42,0.15)",
          }}
        >
          {loading ? "Refreshing..." : "Refresh Dashboard"}
        </button>
      </div>

      {message ? (
        <div
          style={{
            border: "2px solid #ff4d4f",
            borderRadius: "18px",
            padding: "18px 20px",
            marginBottom: "24px",
            background: "white",
            color: "#111827",
            fontSize: "18px",
          }}
        >
          {message}
        </div>
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "20px",
          marginBottom: "28px",
        }}
      >
        <StatCard title="Total Clients" value={stats.total_clients} subtitle="Active 
client records" />
        <StatCard title="Total Letters" value={stats.total_letters} 
subtitle="Generated letters" />
        <StatCard title="Total Disputes" value={stats.total_disputes} subtitle="Saved 
dispute items" />
        <StatCard title="30-Day Letters" value={stats.letters_last_30_days} 
subtitle="Recent production" />
        <StatCard title="Total Tasks / Notes" value={stats.total_notes_tasks} 
subtitle="Workflow items" />
        <StatCard title="Documents" value={stats.total_documents} subtitle="Uploaded 
files" />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: "24px",
          marginTop: "24px",
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: "24px",
            padding: "24px",
            border: "1px solid #e2e8f0",
          }}
        >
          <h2 style={{ fontSize: "28px", fontWeight: "800", marginBottom: "20px" }}>
            Recent Letters
          </h2>

          {(activity.recent_letters || []).slice(0, 5).map((item) => (
            <div
              key={item.id}
              style={{
                padding: "14px",
                borderRadius: "14px",
                background: "#f8fafc",
                marginBottom: "12px",
              }}
            >
              <div style={{ fontWeight: "700" }}>
                {item.template}
              </div>

              <div style={{ color: "#64748b", marginTop: "4px" }}>
                {item.first_name} {item.last_name}
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            background: "white",
            borderRadius: "24px",
            padding: "24px",
            border: "1px solid #e2e8f0",
          }}
        >
          <h2 style={{ fontSize: "28px", fontWeight: "800", marginBottom: "20px" }}>
            Tasks Due
          </h2>

          {activity.tasks_due?.length === 0 ? (
            <div style={{ color: "#64748b" }}>
              No pending tasks
            </div>
          ) : (
            (activity.tasks_due || []).slice(0, 5).map((task) => (
              <div
                key={task.id}
                style={{
                  padding: "14px",
                  borderRadius: "14px",
                  background: "#f8fafc",
                  marginBottom: "12px",
                }}
              >
                <div style={{ fontWeight: "700" }}>
                  {task.title}
                </div>

                <div style={{ color: "#64748b", marginTop: "4px" }}>
                  Due: {task.due_date}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.3fr 1fr",
          gap: "20px",
          marginBottom: "28px",
        }}
      >
        <div style={panelStyle}>
          <div style={panelHeaderStyle}>CRM Activity Overview</div>
          <div style={{ color: "#6b7280", fontSize: "16px", marginBottom: "20px" }}>
            High-level view of client activity across the system.
          </div>

          <div style={{ display: "grid", gap: "18px" }}>
            <MetricRow
              label="Task Completion"
              value={`${completionPercent}%`}
              hint={`${stats.completed_tasks} completed out of 
          ${stats.total_notes_tasks}`}
            />
            <MetricRow
              label="Dispute Pipeline"
              value={`${stats.open_disputes} open / ${stats.pending_disputes} 
          pending`}
              hint={`${stats.resolved_disputes} resolved total`}
            />
          </div>
        </div>

        <div style={panelStyle}>
          <div style={panelHeaderStyle}>Workflow Progress</div>
          <div style={{ color: "#6b7280", fontSize: "16px", marginBottom: "18px" }}>
            Estimated productivity based on completed workflow items.
          </div>

          <div
            style={{
              width: "100%",
              height: "18px",
              background: "#e5e7eb",
              borderRadius: "999px",
              overflow: "hidden",
              marginBottom: "14px",
            }}
          >
            <div
              style={{
                width: `${completionPercent}%`,
                height: "100%",
                background: "linear-gradient(90deg, #D4AF37 0%, #F4C542 100%)",
                borderRadius: "999px",
              }}
            />
          </div>

          <div
            style={{
              fontSize: "40px",
              fontWeight: "800",
              color: "#0f172a",
              marginBottom: "10px",
            }}
          >
            {completionPercent}%
          </div>

          <div style={{ fontSize: "16px", color: "#6b7280", lineHeight: 1.6 }}>
            This progress bar is based on completed notes/tasks across your workflow system.
          </div>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "20px",
          marginBottom: "28px",
        }}
      >
        <div style={panelStyle}>
          <div style={panelHeaderStyle}>Disputes by Status</div>
          <SimpleBar
            items={[
              { label: "Open", value: stats.open_disputes },
              { label: "Pending", value: stats.pending_disputes },
              { label: "Resolved", value: stats.resolved_disputes },
            ]}
          />
        </div>

        <div style={panelStyle}>
          <div style={panelHeaderStyle}>Documents by Type</div>
          <SimpleBar
            items={analytics.documents_by_type.map((item) => ({
              label: item.label || "Other",
              value: item.total || 0,
            }))}
          />
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "20px",
          marginBottom: "28px",
        }}
      >
        <div style={panelStyle}>
          <div style={panelHeaderStyle}>Tasks by Status</div>
          <SimpleBar
            items={analytics.tasks_by_status.map((item) => ({
              label: item.status || "unknown",
              value: item.count || 0,
            }))}
          />
        </div>

        <div style={panelStyle}>
          <div style={panelHeaderStyle}>Disputes by Bureau</div>
          <SimpleBar
            items={analytics.disputes_by_bureau.map((item) => ({
              label: item.bureau || "Unknown",
              value: item.count || 0,
            }))}
          />
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: "20px",
          marginBottom: "28px",
        }}
      >
        <QuickActionCard
          title="Clients"
          description="Create, edit, and organize client records."
          footer={`${stats.total_clients} total clients`}
        />
        <QuickActionCard
          title="Letters"
          description="Generate, review, and manage dispute letters."
          footer={`${stats.total_letters} total letters`}
        />
        <QuickActionCard
          title="Documents"
          description="Store IDs, evidence, and client paperwork."
          footer={`${stats.total_documents} total documents`}
        />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "20px",
          marginBottom: "20px",
        }}
      >
        <div style={panelStyle}>
          <div style={panelHeaderStyle}>Recent Clients</div>

          {recentClients.length === 0 ? (
            <EmptyState text="No clients yet" />
          ) : (
            recentClients.map((client) => (
              <ListRow
                key={client.id}
                title={client.full_name || `Client ${client.id}`}
                line1={client.email || "No email on file"}
                line2={client.phone || "No phone on file"}
              />
            ))
          )}
        </div>

        <div style={panelStyle}>
          <div style={panelHeaderStyle}>Recent Letters</div>

          {recentLetters.length === 0 ? (
            <EmptyState text="No letters yet" />
          ) : (
            recentLetters.map((letter) => (
              <ListRow
                key={letter.id}
                title={letter.letter_type || `Letter ${letter.id}`}
                line1={letter.full_name || `Client ID ${letter.client_id}`}
                line2={letter.bureau || "No bureau selected"}
              />
            ))
          )}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "20px",
          marginBottom: "20px",
        }}
      >
        <div style={panelStyle}>
          <div style={panelHeaderStyle}>Recent Disputes</div>

          {recentDisputes.length === 0 ? (
            <EmptyState text="No disputes yet" />
          ) : (
            recentDisputes.map((dispute) => (
              <ListRow
                key={dispute.id}
                title={`${dispute.bureau || "Unknown Bureau"} Dispute`}
                line1={dispute.full_name || `Client ID ${dispute.client_id}`}
                line2={dispute.status || "open"}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, subtitle }) {
  return (
    <div
      style={{
        background: "white",
        borderRadius: "24px",
        padding: "24px",
        boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
        border: "1px solid #e5e7eb",
      }}
    >
      <div
        style={{
          fontSize: "16px",
          color: "#64748b",
          marginBottom: "16px",
          fontWeight: "600",
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontSize: "56px",
          fontWeight: "800",
          lineHeight: 1,
          color: "#0f172a",
          marginBottom: "10px",
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: "15px",
          color: "#94a3b8",
        }}
      >
        {subtitle}
      </div>
    </div>
  );
}

function QuickActionCard({ title, description, footer }) {
  return (
    <div
      style={{
        background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
        borderRadius: "24px",
        padding: "24px",
        border: "1px solid #dbeafe",
        boxShadow: "0 10px 24px rgba(37,99,235,0.06)",
      }}
    >
      <div
        style={{
          fontSize: "28px",
          fontWeight: "800",
          color: "#0f172a",
          marginBottom: "10px",
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontSize: "16px",
          color: "#64748b",
          lineHeight: 1.6,
          marginBottom: "18px",
        }}
      >
        {description}
      </div>
      <div
        style={{
          display: "inline-block",
          padding: "10px 14px",
          borderRadius: "999px",
          background: "#FFF8E1",
          color: "#A67C00",
          fontSize: "14px",
          fontWeight: "700",
        }}
      >
        {footer}
      </div>
    </div>
  );
}

function MetricRow({ label, value, hint }) {
  return (
    <div
      style={{
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
        borderRadius: "18px",
        padding: "18px",
      }}
    >
      <div
        style={{
          fontSize: "16px",
          fontWeight: "700",
          color: "#0f172a",
          marginBottom: "6px",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: "28px",
          fontWeight: "800",
          color: "#A67C00",
          marginBottom: "6px",
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: "14px",
          color: "#64748b",
        }}
      >
        {hint}
      </div>
    </div>
  );
}

function ListRow({ title, line1, line2 }) {
  return (
    <div
      style={{
        padding: "18px 0",
        borderTop: "1px solid #eef2f7",
      }}
    >
      <div
        style={{
          fontSize: "20px",
          fontWeight: "700",
          color: "#0f172a",
          marginBottom: "6px",
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontSize: "15px",
          color: "#64748b",
          marginBottom: "4px",
        }}
      >
        {line1}
      </div>
      <div
        style={{
          fontSize: "15px",
          color: "#94a3b8",
        }}
      >
        {line2}
      </div>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div
      style={{
        padding: "24px 0",
        color: "#94a3b8",
        fontSize: "16px",
      }}
    >
      {text}
    </div>
  );
}

function SimpleBar({ items }) {
  const safeItems = Array.isArray(items) ? items : [];
  const maxValue = safeItems.length ? Math.max(...safeItems.map((item) => item.value 
|| 0), 1) : 1;

  if (!safeItems.length) {
    return <EmptyState text="No data yet" />;
  }

  return (
    <div style={{ display: "grid", gap: "14px" }}>
      {safeItems.map((item, index) => (
        <div key={`${item.label}-${index}`}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "6px",
              fontSize: "15px",
              color: "#475569",
              fontWeight: "600",
            }}
          >
            <span>{item.label}</span>
            <span>{item.value}</span>
          </div>
          <div
            style={{
              width: "100%",
              height: "12px",
              background: "#e5e7eb",
              borderRadius: "999px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${Math.max(8, Math.round(((item.value || 0) / maxValue) * 
100))}%`,
                height: "100%",
                background: "linear-gradient(90deg, #D4AF37 0%, #F4C542 100%)",
                borderRadius: "999px",
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function capitalize(value) {
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatDate(value) {
  if (!value) return "Not available";
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
  } catch {
    return value;
  }
}

const panelStyle = {
  background: "white",
  borderRadius: "24px",
  padding: "24px",
  border: "1px solid #e5e7eb",
  boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
};

const panelHeaderStyle = {
  fontSize: "30px",
  fontWeight: "800",
  color: "#0f172a",
  marginBottom: "12px",
};
